/**
 * How Portfolio Performance charts write a date on their axis and in a tooltip: month and two-digit year, upper case, "AUG-26" / "SEP-18".
 *
 * The labels reach the charts in several styles, all of which carry a month:
 *   "2026-08-31"   an ISO file date (also with a time)        "2026-08"    a year and month
 *   "Aug'26"       the month label the API builds              "Oct 13'26"  the same for a day that is not a month end
 *   "Sep 2026" / "Sep-26"   written out
 * Anything else ("2024", "12mo", "Current QTD", an industry name) is not a month and is left exactly as it is, so a chart can hold both.
 */
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"] as const;

interface ChartDate {
  year: number;
  /** 1-12 */
  month: number;
  day?: number;
}

function monthFromName(name: string): number | null {
  const i = MONTHS.indexOf(name.toUpperCase() as (typeof MONTHS)[number]);
  return i >= 0 ? i + 1 : null;
}

const year4 = (yy: string) => (yy.length === 2 ? 2000 + Number(yy) : Number(yy));

export function parseChartDate(label: string): ChartDate | null {
  let m = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/.exec(label);
  if (m && Number(m[2]) >= 1 && Number(m[2]) <= 12) return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  m = /^(\d{4})-(\d{2})$/.exec(label);
  if (m && Number(m[2]) >= 1 && Number(m[2]) <= 12) return { year: Number(m[1]), month: Number(m[2]) };
  m = /^([A-Za-z]{3})'(\d{2})$/.exec(label);
  if (m && monthFromName(m[1])) return { year: year4(m[2]), month: monthFromName(m[1])! };
  m = /^([A-Za-z]{3}) (\d{1,2})'(\d{2})$/.exec(label);
  if (m && monthFromName(m[1])) return { year: year4(m[3]), month: monthFromName(m[1])!, day: Number(m[2]) };
  m = /^([A-Za-z]{3})(?: |-)(\d{4})$/.exec(label) ?? /^([A-Za-z]{3})-(\d{2})$/.exec(label);
  if (m && monthFromName(m[1])) return { year: year4(m[2]), month: monthFromName(m[1])! };
  return null;
}

const yy = (year: number) => String(year % 100).padStart(2, "0");

/** A dated label as "AUG-26"; a label that is not a month is returned unchanged. */
export function monthYear(label: string): string {
  const d = parseChartDate(label);
  return d ? `${MONTHS[d.month - 1]}-${yy(d.year)}` : label;
}

/** A dated label with its day, "31-AUG-26", when it has one; otherwise as monthYear. */
export function dayMonthYear(label: string): string {
  const d = parseChartDate(label);
  return d && d.day ? `${String(d.day).padStart(2, "0")}-${monthYear(label)}` : monthYear(label);
}

/**
 * One tick per year, at that year's LAST point in the series (the labels are in date order): December where the data has it, so a past
 * year reads "DEC-24", and the latest month for the year still running, "AUG-26". `undefined` when the labels are not all dates, so a
 * chart that mixes in other labels keeps its own ticks.
 */
export function yearEndTicks(labels: string[]): string[] | undefined {
  const lastOfYear = new Map<number, string>();
  for (const label of labels) {
    const d = parseChartDate(label);
    if (!d) return undefined;
    lastOfYear.set(d.year, label);
  }
  return lastOfYear.size ? [...lastOfYear.values()] : undefined;
}

/**
 * The x axis of a chart whose points may be dates. Every dated label is written "AUG-26". A series can hold several points in one month
 * (one point per day), and a label per point would repeat "AUG-26" down the axis, so when that happens:
 *   - `ticks` lists the first point of each month (plus every label that is not a month), so each month is named once;
 *   - `label` (the tooltip's heading) adds the day to a point whose month holds more than one ("31-AUG-26"), so two points of one month
 *     can still be told apart.
 * With one point per month `ticks` is undefined and the chart keeps its own tick choice.
 */
export function dateAxis(labels: string[]) {
  const monthKey = (label: string) => {
    const d = parseChartDate(label);
    return d ? `${d.year}-${d.month}` : null;
  };
  const perMonth = new Map<string, number>();
  const firsts: string[] = [];
  for (const label of labels) {
    const key = monthKey(label);
    if (key === null) {
      firsts.push(label);
      continue;
    }
    if (!perMonth.has(key)) firsts.push(label);
    perMonth.set(key, (perMonth.get(key) ?? 0) + 1);
  }
  const crowded = [...perMonth.values()].some((n) => n > 1);
  return {
    ticks: crowded ? firsts : undefined,
    tick: monthYear,
    label: (label: string) => {
      const key = monthKey(label);
      return crowded && key !== null && (perMonth.get(key) ?? 0) > 1 ? dayMonthYear(label) : monthYear(label);
    },
  };
}
